import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import type { CreditType } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useMenu } from '../../../../../hooks/use-menu';
import { runMutation } from '../../../../../utils/vy-mutation';
import { invalidateProductsQueries } from '../../../../../utils/query-invalidation';
import { useDeleteCreditType } from '../../../../../hooks/requests/use-delete-credit-type';
import { UpsertCreditTypeDialog } from '../CreateProductDialog/UpsertCreditTypeDialog';
import { useToggle } from '../../../../../hooks/use-toggle';

type CreditTypesTableActionsProps = {
  creditTypeId: string;
  row: CreditType;
  disableDelete: boolean;
  creditTypeName?: string;
};

export const CreditTypesTableActions: React.FC<CreditTypesTableActionsProps> = ({
  creditTypeId,
  row,
  disableDelete,
  creditTypeName,
}) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const { anchorElement, isOpen, handleMenuOpen, handleMenuClose } = useMenu();
  const [isEditDialogOpen, toggleIsEditDialogOpen] = useToggle(false);

  const queryClient = useQueryClient();

  const deleteCreditTypeMutation = useDeleteCreditType({
    onSuccess: () => invalidateProductsQueries(queryClient),
  });

  const handleEditClick = () => {
    handleMenuClose();
    toggleIsEditDialogOpen();
  };

  const handleEditDialogClose = () => {
    toggleIsEditDialogOpen();
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    handleOpen();
  };

  const handleDelete = async () => {
    await runMutation(
      deleteCreditTypeMutation,
      { creditTypeId },
      {
        pending: 'Deleting credit product...',
        success: 'Credit product successfully deleted',
        error: 'Failed to delete credit product',
      },
      {
        onSuccess: async () => {
          await invalidateProductsQueries(queryClient);
        },
      },
    );
  };

  return (
    <>
      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        label={`Delete ${creditTypeName}`}
        labelDescription="Are you sure you want to delete this product?"
        ctaText="Delete"
        warningText="This action is final and cannot be reversed"
        cancelButtonText="Cancel"
        onSubmit={handleDelete}
        disableDividers={true}
      />

      <UpsertCreditTypeDialog
        openCreateProductDialog={isEditDialogOpen}
        onClose={handleEditDialogClose}
        editCreditType={row}
      />

      <IconButton size="medium" onClick={handleMenuOpen} data-vy="credit-types-table-actions-edit">
        <VyIcon icon="DotsMenu" style={{ transform: 'rotate(90deg)' }} />
      </IconButton>

      <Menu
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick} data-vy="credit-types-table-actions-edit">
          <ListItemIcon>
            <VyIcon icon="Edit" style={{ transform: 'scale(0.8)' }} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <Tooltip
          title={disableDelete ? 'This credit type is in use and cannot be deleted' : ''}
          placement="left"
        >
          <span>
            <MenuItem
              onClick={handleDeleteClick}
              disabled={disableDelete}
              data-vy="credit-types-table-actions-delete"
            >
              <ListItemIcon>
                <VyIcon icon="Delete" style={{ transform: 'scale(0.8)' }} />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </>
  );
};
